/** Home: new-hire checklist. Upload steps take a photo/PDF, ack steps are a tick, info steps are done by the office. */
import { useEffect, useState } from "react";
import { ClipboardCheck, Check, Upload, Loader2, Building2, Undo2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const openFile = (path) => api.get(path, { responseType: "blob" }).then(({ data }) => window.open(URL.createObjectURL(data), "_blank")).catch(() => toast.error("Could not open the file"));

export default function OnboardingCard() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/onboarding/me").then(({ data }) => setD(data)).catch(() => setD(null));
  useEffect(() => { load(); }, []);
  if (!d || d.complete) return null;

  const mark = async (step, file) => {
    setBusy(step.id);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      await api.post(`/onboarding/me/${step.id}`, fd);
      toast.success(`${step.title} — done`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(""); }
  };
  const undo = async (step) => { try { await api.delete(`/onboarding/me/${step.id}`); load(); } catch { toast.error("Could not undo"); } };
  const pct = d.required ? Math.round((d.done / d.required) * 100) : 0;

  return (
    <div className="rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-4" data-testid="onboarding-card">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="h-5 w-5 text-sky-300" />
        <div className="flex-1">
          <p className="text-sm font-bold text-white">Getting started — {d.done} of {d.required} required steps done</p>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-white/10"><div className="h-full bg-sky-400 transition-[width]" style={{ width: `${pct}%` }} /></div>
        </div>
      </div>
      <ul className="mt-3 space-y-1.5">
        {d.steps.map((s) => (
          <li key={s.id} className={`flex flex-wrap items-center gap-2 rounded-lg border px-3 py-2 text-xs ${s.done ? "border-emerald-500/30 bg-emerald-500/10 text-white/60" : "border-white/10 bg-white/[0.03] text-white/80"}`} data-testid={`onboarding-step-${s.id}`}>
            <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${s.done ? "border-emerald-400 bg-emerald-500 text-white" : "border-white/30"}`}>{s.done && <Check className="h-3 w-3" />}</span>
            <span className="flex-1">
              <span className={`font-semibold ${s.done ? "line-through" : ""}`}>{s.title}</span>{!s.required && <span className="ml-1 text-white/40">(optional)</span>}
              {s.detail && <span className="block text-white/40">{s.detail}</span>}
              {s.done?.file_name && <button onClick={() => openFile(`/onboarding/file/${encodeURIComponent(s.done.by)}/${s.id}`)} className="text-sky-300 underline">{s.done.file_name}</button>}
            </span>
            {s.done ? (
              s.kind !== "info" && <button onClick={() => undo(s)} className="inline-flex items-center gap-1 rounded-md bg-white/10 px-2 py-1 text-white/70 hover:bg-white/15" data-testid={`onboarding-undo-${s.id}`}><Undo2 className="h-3 w-3" /> Undo</button>
            ) : s.kind === "upload" ? (
              <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-sky-600 px-2.5 py-1 font-semibold text-white hover:bg-sky-500">
                {busy === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />} Upload
                <input type="file" accept="image/*,application/pdf" className="hidden" data-testid={`onboarding-upload-${s.id}`} onChange={(e) => e.target.files?.[0] && mark(s, e.target.files[0])} />
              </label>
            ) : s.kind === "ack" ? (
              <button onClick={() => mark(s)} disabled={busy === s.id} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white hover:bg-emerald-500 disabled:opacity-50" data-testid={`onboarding-ack-${s.id}`}>
                {busy === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />} Done
              </button>
            ) : (
              <span className="inline-flex items-center gap-1 text-white/40"><Building2 className="h-3 w-3" /> office</span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
